import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  TrendingUp, 
  Heart,
  Play,
  ListMusic,
  User,
  Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CommunityPage = () => {
  const { getAuthHeaders } = useAuth();
  const [weeklyMedleys, setWeeklyMedleys] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWeeklyMedleys();
  }, []);

  const fetchWeeklyMedleys = async () => {
    try {
      const response = await fetch(`${API}/community/weekly?limit=10`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setWeeklyMedleys(data.medleys || []);
      }
    } catch (error) {
      console.error('Error fetching weekly medleys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (medleyId) => {
    try {
      const response = await fetch(`${API}/medleys/${medleyId}/like`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setWeeklyMedleys(prev =>
          prev.map(m => m.medley_id === medleyId ? { ...m, likes: m.likes + 1 } : m)
        );
      }
    } catch (error) {
      console.error('Error liking medley:', error);
    }
  };

  const getKeyBadgeClass = (key) => {
    const keyMap = {
      'C': 'bg-key-c', 'G': 'bg-key-g', 'D': 'bg-key-d',
      'A': 'bg-key-a', 'E': 'bg-key-e', 'B': 'bg-key-b',
      'F#': 'bg-key-fsharp', 'Db': 'bg-key-db', 'Ab': 'bg-key-ab',
      'Eb': 'bg-key-eb', 'Bb': 'bg-key-bb', 'F': 'bg-key-f'
    };
    const baseKey = key?.replace('m', '');
    return keyMap[baseKey] || 'bg-primary';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="community-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <Users className="w-8 h-8 text-primary" />
            Comunidad
          </h1>
          <p className="text-muted-foreground mt-1">
            Descubre los medleys más populares de la semana
          </p>
        </div>
      </div>

      {/* Weekly Trending */}
      <Card className="border-border/50 overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-accent/10">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-accent" />
            Medleys de la Semana
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-muted rounded-lg animate-pulse" />
              ))}
            </div>
          ) : weeklyMedleys.length > 0 ? (
            <div className="divide-y divide-border">
              {weeklyMedleys.map((medley, index) => (
                <div
                  key={medley.medley_id}
                  className="p-4 md:p-6 hover:bg-muted/30 transition-colors"
                  data-testid={`weekly-medley-${medley.medley_id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Rank */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                      index === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                      index === 1 ? 'bg-gray-400/20 text-gray-400' :
                      index === 2 ? 'bg-amber-600/20 text-amber-600' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {index + 1}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Link to={`/medleys/${medley.medley_id}`}>
                          <h3 className="font-heading font-semibold text-lg hover:text-primary transition-colors">
                            {medley.title}
                          </h3>
                        </Link>
                        {medley.global_key && (
                          <Badge className={getKeyBadgeClass(medley.global_key)}>
                            {medley.global_key}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {medley.author_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <ListMusic className="w-4 h-4" />
                          {medley.songs?.length || 0} canciones
                        </span>
                      </div>
                      
                      {/* Songs preview */}
                      <div className="flex flex-wrap gap-2">
                        {medley.songs?.slice(0, 4).map((song, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {song.title}
                          </Badge>
                        ))}
                        {medley.songs?.length > 4 && (
                          <Badge variant="outline" className="text-xs">
                            +{medley.songs.length - 4} más
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1"
                        onClick={() => handleLike(medley.medley_id)}
                        data-testid={`like-btn-${medley.medley_id}`}
                      >
                        <Heart className="w-4 h-4" />
                        {medley.likes}
                      </Button>
                      <Link to={`/singer?medley=${medley.medley_id}`}>
                        <Button variant="outline" size="sm" className="gap-1">
                          <Play className="w-4 h-4" />
                          Presentar
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ListMusic className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No hay medleys esta semana</h3>
              <p className="text-muted-foreground mb-4">
                Sé el primero en crear y compartir un medley
              </p>
              <Link to="/generator">
                <Button>Crear mi primer medley</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info section */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-border/50">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <ListMusic className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-semibold mb-1">Crea Medleys</h3>
            <p className="text-sm text-muted-foreground">
              Combina canciones con transiciones armónicas suaves
            </p>
          </CardContent>
        </Card>
        
        <Card className="border-border/50">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Users className="w-6 h-6 text-accent" />
            </div>
            <h3 className="font-semibold mb-1">Comparte</h3>
            <p className="text-sm text-muted-foreground">
              Publica tus medleys para que otros músicos los usen
            </p>
          </CardContent>
        </Card>
        
        <Card className="border-border/50">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Heart className="w-6 h-6 text-destructive" />
            </div>
            <h3 className="font-semibold mb-1">Vota</h3>
            <p className="text-sm text-muted-foreground">
              Dale like a los mejores medleys de la comunidad
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CommunityPage;
